import { formatCurrency, formatNumber } from 'modules/I18N';
import { ProductTarget, ProductTargets, SelectionPermission } from 'store/productManagement/reducer.types';
import moneySvg from 'assets/icons/money.svg';
import framesSvg from 'assets/icons/frame.svg';
import eyeSvg from 'assets/icons/eye_empty.svg';
import { SectionSelectedType } from 'lib/SectionButton/SectionButton.types';
import { PlannerSecondaryPanelType } from 'store/dealManagement/types';
import { DirectSalesCodeNameModel } from 'components/common/types/DirectSalesCampaign.types';
import { getNoRestrictionsDisplayValue } from '../../utils/getNoRestrictionsDisplayValue';
import { Target } from './ProductTargetsButton.types';

export const hasTargets = ({ budget, frames, impressions }: ProductTargets): boolean => {
  return Boolean(
    budget?.value ||
      budget?.selectionPermission ||
      frames?.value ||
      frames?.selectionPermission ||
      impressions?.value ||
      impressions?.selectionPermission,
  );
};

const getTargetValue = (target: ProductTarget, localeCode: string, currencyCode?: string): SectionSelectedType => {
  const { defaultValue, minValue, maxValue, value } = target;

  if (value) {
    return currencyCode ? [formatCurrency({ value, localeCode, currencyCode })] : [formatNumber({ value, localeCode })];
  }

  return {
    ...(defaultValue && {
      Default: {
        included: [
          {
            name: currencyCode
              ? formatCurrency({ value: defaultValue, localeCode, currencyCode })
              : formatNumber({ value: defaultValue, localeCode }),
            code: 'Default',
          },
        ],
      },
    }),
    ...(minValue && {
      Minimum: {
        included: [
          {
            name: currencyCode
              ? formatCurrency({ value: minValue, localeCode, currencyCode })
              : formatNumber({ value: minValue, localeCode }),
            code: 'Minimum',
          },
        ],
      },
    }),
    ...(maxValue && {
      Maximum: {
        included: [
          {
            name: currencyCode
              ? formatCurrency({ value: maxValue, localeCode, currencyCode })
              : formatNumber({ value: maxValue, localeCode }),
            code: 'Maximum',
          },
        ],
      },
    }),
  };
};

export const getTargetData = ({
  permission,
  value,
  priority,
  greenTolerance,
  orangeTolerance,
  minValue,
  maxValue,
  defaultValue,
}: {
  permission: SelectionPermission;
  value?: number;
  priority?: DirectSalesCodeNameModel;
  greenTolerance?: number;
  orangeTolerance?: number;
  minValue?: number;
  maxValue?: number;
  defaultValue?: number;
}): ProductTarget => {
  if (permission === SelectionPermission.FIXED_VALUE) {
    return {
      selectionPermission: permission,
      value,
      priority,
      greenTolerance,
      orangeTolerance,
    };
  }

  if (permission === SelectionPermission.RANGE_VALUE) {
    return {
      selectionPermission: permission,
      minValue,
      maxValue,
      defaultValue,
      priority,
      greenTolerance,
      orangeTolerance,
    };
  }

  return {
    selectionPermission: permission,
  };
};

export const groupTargets = (
  { budget, frames, impressions }: ProductTargets,
  localeCode: string,
  currencyCode: string,
): Target[] => {
  const isTargetFilled = (target?: ProductTarget): boolean => {
    if (!target) return false;

    return (
      target.selectionPermission === SelectionPermission.FIXED_VALUE ||
      target.selectionPermission === SelectionPermission.RANGE_VALUE
    );
  };

  const getValue = (target?: ProductTarget, isCurrency?: boolean): SectionSelectedType | undefined => {
    if (!target) return undefined;

    if (isTargetFilled(target)) {
      return getTargetValue(target, localeCode, isCurrency ? currencyCode : undefined);
    }

    return [getNoRestrictionsDisplayValue(target?.selectionPermission)[0]];
  };

  return [
    {
      key: 'budget',
      label: 'Budget',
      value: getValue(budget, true),
      icon: moneySvg,
    },
    {
      key: 'frames',
      label: 'Frames',
      value: getValue(frames),
      icon: framesSvg,
    },
    {
      key: 'impressions',
      label: 'Impressions',
      value: getValue(impressions),
      icon: eyeSvg,
    },
  ];
};

const isValueRequired = (permission: SelectionPermission): boolean => {
  return [SelectionPermission.ALL_OPTIONS, SelectionPermission.FIXED_VALUE, SelectionPermission.RANGE_VALUE].includes(
    permission,
  );
};

export const isSaveDisabled = ({
  budget,
  frames,
  impressions,
  selectedBudgetPermission,
  selectedFramesPermission,
  selectedImpressionsPermission,
}: {
  budget?: {
    value?: number;
    minValue?: number;
    maxValue?: number;
  };
  frames?: {
    value?: number;
    minValue?: number;
    maxValue?: number;
  };
  impressions?: {
    value?: number;
    minValue?: number;
    maxValue?: number;
  };
  selectedBudgetPermission: SelectionPermission;
  selectedFramesPermission: SelectionPermission;
  selectedImpressionsPermission: SelectionPermission;
}): boolean => {
  const isBudgetValueRequired = isValueRequired(selectedBudgetPermission);
  const isFramesValueRequired = isValueRequired(selectedFramesPermission);
  const isImpressionsValueRequired = isValueRequired(selectedImpressionsPermission);

  let isFramesCorrect = true;

  if (selectedFramesPermission === SelectionPermission.FIXED_VALUE) isFramesCorrect = !!frames?.value;
  if (selectedFramesPermission === SelectionPermission.RANGE_VALUE)
    isFramesCorrect = !!frames?.minValue || !!frames?.maxValue;

  let isBudgetCorrect = true;
  if (selectedBudgetPermission === SelectionPermission.FIXED_VALUE) isBudgetCorrect = !!budget?.value;
  if (selectedBudgetPermission === SelectionPermission.RANGE_VALUE)
    isBudgetCorrect = !!budget?.minValue || !!budget?.maxValue;

  let isImpressionsCorrect = true;
  if (selectedImpressionsPermission === SelectionPermission.FIXED_VALUE) isImpressionsCorrect = !!impressions?.value;
  if (selectedImpressionsPermission === SelectionPermission.RANGE_VALUE)
    isImpressionsCorrect = !!impressions?.minValue || !!impressions?.maxValue;

  const areAllTargetsCorrect = isFramesCorrect && isBudgetCorrect && isImpressionsCorrect;

  return !((isBudgetValueRequired || isFramesValueRequired || isImpressionsValueRequired) && areAllTargetsCorrect);
};

export const shouldSkipNextRequiredPanelException = (
  selectedPermission: SelectionPermission,
  currentPanel: PlannerSecondaryPanelType,
  nextRequiredPanel: PlannerSecondaryPanelType,
): boolean => {
  if (currentPanel === PlannerSecondaryPanelType.NETWORKS) {
    if (selectedPermission === SelectionPermission.DISABLED) {
      return false;
    }

    if (nextRequiredPanel === PlannerSecondaryPanelType.TARGETS) {
      return true;
    }

    return (
      nextRequiredPanel === PlannerSecondaryPanelType.PRICING_MODE &&
      selectedPermission !== SelectionPermission.ALL_OPTIONS
    );
  }

  return currentPanel === PlannerSecondaryPanelType.TARGETS && nextRequiredPanel === PlannerSecondaryPanelType.NETWORKS;
};
